import {
    Badge,
    Button,
    Group,
    Loader,
    SegmentedControl,
    Select,
    Stack,
    Table,
    Text,
    TextInput,
    Tooltip,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { AttractionReadinessLevelEnum } from '@routellers/types/dist/city-attraction/attraction-readiness';
import { CityAttractionRO } from '@routellers/types/dist/city-attraction/city-attraction.ro';
import { CityAttractionLocaleRO } from '@routellers/types/dist/city-attraction/city-attraction-locale.ro';
import {
    AttractionAudioPotentialEnum,
    AUDIO_POTENTIAL_UNASSESSED,
} from '@routellers/types/dist/city-attraction/enum/attraction-audio-potential.enum';
import { AttractionCategoryEnum } from '@routellers/types/dist/city-attraction/enum/attraction-category.enum';
import { AttractionReviewStatusEnum } from '@routellers/types/dist/city-attraction/enum/attraction-review-status.enum';
import { AttractionScenarioEnum } from '@routellers/types/dist/city-attraction/enum/attraction-scenario.enum';
import { AttractionThemeEnum } from '@routellers/types/dist/city-attraction/enum/attraction-theme.enum';
import { CatalogRestoreCandidateRO } from '@routellers/types/dist/city-collection/city-collection.ro';
import { Languages } from '@routellers/types/dist/shared/enum/languages.enum';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { api, apiErrorMessage } from '../api';
import { AttractionReadinessProgress } from '../components/attraction-readiness-badge';
import { AttractionsMap } from '../components/attractions-map';
import { CityPipelineBar } from '../components/city-pipeline-bar';
import { CityQuickSelect, parseCityFilter } from '../components/city-quick-select';
import { EntityThumb } from '../components/entity-thumb';
import { HelpHint, MenuHelpIcon } from '../components/help-hint';
import { ListPagination } from '../components/list-pagination';
import { PageHeader } from '../components/page-header';
import { AttractionEditor } from '../editors/attraction-editor';
import { enumLabel, enumOptions } from '../i18n';
import { contentLanguagesForCountry } from '../utils/content-languages';
import { coverFromPhotos, LIST_PAGE_SIZE, totalFromMeta } from '../utils/list';

const ALL = 'all';

const reviewStatusFromSearch = (value: string | null): string => {
    if (
        value &&
        Object.values(AttractionReviewStatusEnum).includes(value as AttractionReviewStatusEnum)
    ) {
        return value;
    }
    return ALL;
};

const reviewColor: Record<AttractionReviewStatusEnum, string> = {
    [AttractionReviewStatusEnum.APPROVED]: 'green',
    [AttractionReviewStatusEnum.NEED_REVIEW]: 'orange',
    [AttractionReviewStatusEnum.REJECTED]: 'red',
    [AttractionReviewStatusEnum.REJECTED_MCP]: 'red',
};

const STATUS_FILTER_ORDER = [
    AttractionReviewStatusEnum.NEED_REVIEW,
    AttractionReviewStatusEnum.APPROVED,
    AttractionReviewStatusEnum.REJECTED,
    AttractionReviewStatusEnum.REJECTED_MCP,
];

const READINESS_FILTER_ORDER = [
    AttractionReadinessLevelEnum.NONE,
    AttractionReadinessLevelEnum.TRIAGED,
    AttractionReadinessLevelEnum.PASSPORT,
    AttractionReadinessLevelEnum.SCOUTED,
    AttractionReadinessLevelEnum.LIGHT,
    AttractionReadinessLevelEnum.BASE,
    AttractionReadinessLevelEnum.BRIEF,
    AttractionReadinessLevelEnum.GUIDE,
    AttractionReadinessLevelEnum.AUDIO,
];

/** Full package: hook + guideTalk (same bar as fill-complete pipeline). */
const isAttractionLocaleReady = (locale: CityAttractionLocaleRO) =>
    !!locale.content?.hook?.trim() && !!locale.content?.guideTalk?.trim();

const readyAttractionLanguages = (row: CityAttractionRO): Set<Languages> =>
    new Set((row.locales || []).filter(isAttractionLocaleReady).map((locale) => locale.language));

export const AttractionsPage = () => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const [rows, setRows] = useState<CityAttractionRO[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [searchText] = useDebouncedValue(search.trim(), 300);
    const [view, setView] = useState('list');
    const [revision, setRevision] = useState(0);
    const listRequest = useRef<AbortController | null>(null);
    const refresh = () => setRevision((value) => value + 1);
    const [reviewStatus, setReviewStatus] = useState<string>(() =>
        reviewStatusFromSearch(searchParams.get('reviewStatus')),
    );
    const [readinessLevel, setReadinessLevel] = useState(ALL);
    const [category, setCategory] = useState(ALL);
    const [theme, setTheme] = useState(ALL);
    const [scenario, setScenario] = useState(ALL);
    const [audioPotential, setAudioPotential] = useState(ALL);
    const [minMustSee, setMinMustSee] = useState(ALL);
    const [storyRejects, setStoryRejects] = useState<CatalogRestoreCandidateRO[] | null>(null);
    const [cityFilter, setCityFilter] = useState('all');
    const [editorId, setEditorId] = useState<number | null>(() => {
        const id = Number(searchParams.get('edit'));
        return Number.isInteger(id) && id > 0 ? id : null;
    });
    const [creating, setCreating] = useState(false);

    const where = useMemo(() => {
        const filters: Record<string, unknown> = {};
        if (reviewStatus !== ALL) filters.reviewStatus = reviewStatus;
        if (readinessLevel !== ALL) filters.readinessLevel = readinessLevel;
        if (category !== ALL) filters.category = category;
        if (theme !== ALL) filters.theme = theme;
        if (scenario !== ALL) filters.scenario = scenario;
        if (audioPotential !== ALL) filters.audioPotential = audioPotential;
        if (minMustSee !== ALL) filters.minMustSee = Number(minMustSee);
        const cityId = parseCityFilter(cityFilter);
        if (cityId) filters.cityId = cityId;
        if (!Object.keys(filters).length) filters.id = null;
        return filters;
    }, [
        cityFilter,
        reviewStatus,
        readinessLevel,
        category,
        theme,
        scenario,
        audioPotential,
        minMustSee,
    ]);

    const load = useCallback(async () => {
        if (view !== 'list') return;
        listRequest.current?.abort();
        const controller = new AbortController();
        listRequest.current = controller;
        setLoading(true);
        try {
            const { data } = await api.cityAttractionsApi.query({
                page,
                limit: LIST_PAGE_SIZE,
                searchText: searchText || undefined,
                where,
                signal: controller.signal,
            });
            if (controller.signal.aborted) return;
            setRows(data?.items || []);
            setTotal(totalFromMeta(data?.meta, data?.items?.length || 0));
        } catch (error) {
            if (controller.signal.aborted) return;
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
            setRows([]);
            setTotal(0);
        } finally {
            if (!controller.signal.aborted) setLoading(false);
        }
    }, [view, page, searchText, where]);

    useEffect(() => {
        // A successful save/pipeline refresh also invalidates the active view.
        void revision;
        load();
        return () => listRequest.current?.abort();
    }, [load, revision]);

    return (
        <Stack gap='md' style={{ flex: 1, minHeight: 0 }}>
            <PageHeader
                title={t('pages.attractions.title')}
                action={
                    <Group gap='sm'>
                        <SegmentedControl
                            aria-label={t('pages.attractions.viewLabel')}
                            value={view}
                            onChange={(value) => setView(String(value))}
                            data={[
                                { value: 'list', label: t('pages.attractions.listView') },
                                { value: 'map', label: t('pages.attractions.mapView') },
                            ]}
                        />
                        <Button
                            onClick={() => {
                                setEditorId(null);
                                setCreating(true);
                            }}
                        >
                            {t('pages.attractions.create')}
                        </Button>
                    </Group>
                }
            />

            <Group gap='xs' wrap='wrap' align='center'>
                <TextInput
                    placeholder={t('pages.attractions.searchPlaceholder')}
                    value={search}
                    onChange={(e) => {
                        setSearch(e.currentTarget.value);
                        setPage(1);
                    }}
                    w={240}
                />
                <CityQuickSelect
                    value={cityFilter}
                    onChange={(v) => {
                        setPage(1);
                        setCityFilter(v);
                    }}
                />
                <Select
                    w={180}
                    data={[
                        { value: ALL, label: t('pages.attractions.statusAll') },
                        ...STATUS_FILTER_ORDER.map((value) => ({
                            value,
                            label: enumLabel(t, 'enums.attractionReviewStatus', value),
                        })),
                    ]}
                    aria-label={t('pages.attractions.statusAll')}
                    leftSection={
                        <HelpHint
                            subject={t('pages.attractions.statusAll')}
                            label={t('pages.attractions.help.status')}
                        />
                    }
                    leftSectionPointerEvents='all'
                    value={reviewStatus}
                    onChange={(v) => {
                        setPage(1);
                        setReviewStatus(v || ALL);
                    }}
                />
                <Select
                    w={190}
                    data={[
                        { value: ALL, label: t('pages.attractions.readinessAll') },
                        ...READINESS_FILTER_ORDER.map((value) => ({
                            value,
                            label: enumLabel(t, 'enums.attractionReadiness', value),
                        })),
                    ]}
                    renderOption={({ option }) => (
                        <Tooltip
                            label={t(`pages.attractions.readinessHelp.${option.value}`)}
                            multiline
                            maw={340}
                            withArrow
                            position='left'
                            events={{ hover: true, focus: true, touch: true }}
                        >
                            <Group justify='space-between' wrap='nowrap' w='100%'>
                                <span>{option.label}</span>
                                <MenuHelpIcon />
                            </Group>
                        </Tooltip>
                    )}
                    aria-label={t('pages.attractions.readinessAll')}
                    leftSection={
                        <HelpHint
                            subject={t('pages.attractions.readinessAll')}
                            label={t('pages.attractions.help.readiness')}
                        />
                    }
                    leftSectionPointerEvents='all'
                    value={readinessLevel}
                    onChange={(v) => {
                        setPage(1);
                        setReadinessLevel(v || ALL);
                    }}
                />
                <Select
                    w={180}
                    data={[
                        { value: ALL, label: t('pages.attractions.categoryAll') },
                        ...enumOptions(
                            Object.values(AttractionCategoryEnum),
                            t,
                            'enums.attractionCategory',
                        ),
                    ]}
                    value={category}
                    onChange={(v) => {
                        setPage(1);
                        setCategory(v || ALL);
                    }}
                />
                <Select
                    w={180}
                    data={[
                        { value: ALL, label: t('pages.attractions.themeAll') },
                        ...enumOptions(
                            Object.values(AttractionThemeEnum),
                            t,
                            'enums.attractionTheme',
                        ),
                    ]}
                    aria-label={t('pages.attractions.themeAll')}
                    leftSection={
                        <HelpHint
                            subject={t('pages.attractions.themeAll')}
                            label={t('pages.attractions.help.theme')}
                        />
                    }
                    leftSectionPointerEvents='all'
                    value={theme}
                    onChange={(v) => {
                        setPage(1);
                        setTheme(v || ALL);
                    }}
                />
                <Select
                    w={180}
                    data={[
                        { value: ALL, label: t('pages.attractions.scenarioAll') },
                        ...enumOptions(
                            Object.values(AttractionScenarioEnum),
                            t,
                            'enums.attractionScenario',
                        ),
                    ]}
                    aria-label={t('pages.attractions.scenarioAll')}
                    leftSection={
                        <HelpHint
                            subject={t('pages.attractions.scenarioAll')}
                            label={t('pages.attractions.help.scenario')}
                        />
                    }
                    leftSectionPointerEvents='all'
                    value={scenario}
                    onChange={(v) => {
                        setPage(1);
                        setScenario(v || ALL);
                    }}
                />
                <Select
                    w={210}
                    data={[
                        { value: ALL, label: t('pages.attractions.audioAll') },
                        {
                            value: AUDIO_POTENTIAL_UNASSESSED,
                            label: t('enums.attractionAudioPotential.unassessed'),
                        },
                        ...enumOptions(
                            Object.values(AttractionAudioPotentialEnum),
                            t,
                            'enums.attractionAudioPotential',
                        ),
                    ]}
                    aria-label={t('pages.attractions.audioAll')}
                    leftSection={
                        <HelpHint
                            subject={t('pages.attractions.audioAll')}
                            label={t('pages.attractions.help.audio')}
                        />
                    }
                    leftSectionPointerEvents='all'
                    value={audioPotential}
                    onChange={(v) => {
                        setPage(1);
                        setAudioPotential(v || ALL);
                    }}
                />
                <Select
                    w={180}
                    data={[
                        { value: ALL, label: t('pages.attractions.mustSeeAll') },
                        { value: '8', label: t('pages.attractions.mustSeeHigh') },
                    ]}
                    aria-label={t('pages.attractions.mustSeeAll')}
                    leftSection={
                        <HelpHint
                            subject={t('pages.attractions.mustSeeAll')}
                            label={t('pages.attractions.help.mustSee')}
                        />
                    }
                    leftSectionPointerEvents='all'
                    value={minMustSee}
                    onChange={(v) => {
                        setPage(1);
                        setMinMustSee(v || ALL);
                    }}
                />
                <CityPipelineBar cityId={parseCityFilter(cityFilter)} onFinished={refresh} />
                <Group gap={4} wrap='nowrap'>
                    <Button
                        variant='default'
                        disabled={!parseCityFilter(cityFilter)}
                        onClick={async () => {
                            const cityId = parseCityFilter(cityFilter);

                            if (!cityId) return;

                            try {
                                const { data } =
                                    await api.cityAttractionsApi.previewCatalogRestores({
                                        data: { cityId },
                                    });

                                setStoryRejects(data?.items || []);
                            } catch (error) {
                                notifications.show({
                                    color: 'red',
                                    message: apiErrorMessage(error),
                                });
                            }
                        }}
                    >
                        {t('pages.attractions.storyRejects')}
                    </Button>
                    <HelpHint
                        subject={t('pages.attractions.storyRejects')}
                        label={t('pages.attractions.help.storyRejects')}
                    />
                </Group>
            </Group>
            {storyRejects ? (
                <Stack gap={4}>
                    <Text size='sm' c='dimmed'>
                        {t('pages.attractions.storyRejectsHint')}
                    </Text>
                    {storyRejects.length ? (
                        storyRejects.map((item) => (
                            <Text key={item.id} size='sm'>
                                {item.title}
                                {item.rejectionReason ? ` — ${item.rejectionReason}` : ''}
                            </Text>
                        ))
                    ) : (
                        <Text size='sm'>{t('pages.attractions.storyRejectsEmpty')}</Text>
                    )}
                </Stack>
            ) : null}

            {view === 'map' ? (
                <AttractionsMap
                    cityId={parseCityFilter(cityFilter)}
                    where={where}
                    searchText={searchText}
                    revision={revision}
                    onOpen={setEditorId}
                />
            ) : loading ? (
                <Loader />
            ) : (
                <>
                    <Table.ScrollContainer minWidth={720}>
                        <Table
                            striped
                            highlightOnHover
                            withTableBorder
                            verticalSpacing='xs'
                            styles={{ td: { verticalAlign: 'top' } }}
                            style={{ tableLayout: 'fixed', width: '100%' }}
                        >
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th style={{ width: '30%' }}>
                                        {t('pages.attractions.columns.title')}
                                    </Table.Th>
                                    <Table.Th style={{ width: '24%' }}>
                                        <Group gap={4} wrap='nowrap'>
                                            <span>{t('pages.attractions.columns.readiness')}</span>
                                            <HelpHint
                                                subject={t('pages.attractions.columns.readiness')}
                                                label={t('pages.attractions.help.progress')}
                                            />
                                        </Group>
                                    </Table.Th>
                                    <Table.Th style={{ width: '14%' }}>
                                        <Group gap={4} wrap='nowrap'>
                                            <span>{t('pages.attractions.columns.locales')}</span>
                                            <HelpHint
                                                subject={t('pages.attractions.columns.locales')}
                                                label={t('pages.attractions.help.locales')}
                                            />
                                        </Group>
                                    </Table.Th>
                                    <Table.Th style={{ width: '16%' }}>
                                        {t('pages.attractions.columns.category')}
                                    </Table.Th>
                                    <Table.Th>
                                        <Group gap={4} wrap='nowrap'>
                                            <span>{t('pages.attractions.columns.review')}</span>
                                            <HelpHint
                                                subject={t('pages.attractions.columns.review')}
                                                label={t('pages.attractions.help.status')}
                                            />
                                        </Group>
                                    </Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {rows.map((row) => {
                                    const ready = readyAttractionLanguages(row);
                                    const expected = contentLanguagesForCountry(row.country);

                                    return (
                                        <Table.Tr
                                            key={row.id}
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => setEditorId(row.id)}
                                        >
                                            <Table.Td>
                                                <EntityThumb
                                                    src={coverFromPhotos(
                                                        row.photos,
                                                        row.coverPhotoIndex,
                                                    )}
                                                    name={row.displayTitle || row.title}
                                                    subtitle={
                                                        row.cityTitle ||
                                                        (row.cityId ? `#${row.cityId}` : '')
                                                    }
                                                    detail={row.address || row.knownFor || ''}
                                                    radius='md'
                                                />
                                            </Table.Td>
                                            <Table.Td>
                                                <AttractionReadinessProgress
                                                    readiness={row.readiness}
                                                />
                                            </Table.Td>
                                            <Table.Td>
                                                <Group gap={4} wrap='wrap'>
                                                    {expected.map((language) => {
                                                        const filled = ready.has(language);

                                                        return (
                                                            <Badge
                                                                key={language}
                                                                size='xs'
                                                                variant={
                                                                    filled ? 'filled' : 'light'
                                                                }
                                                                color={filled ? 'blue' : 'gray'}
                                                            >
                                                                {language.toUpperCase()}
                                                            </Badge>
                                                        );
                                                    })}
                                                    {!expected.length && (
                                                        <Text size='xs' c='dimmed'>
                                                            —
                                                        </Text>
                                                    )}
                                                </Group>
                                            </Table.Td>
                                            <Table.Td>
                                                <Stack gap={2}>
                                                    <Text size='sm' lh={1.3}>
                                                        {row.category
                                                            ? enumLabel(
                                                                  t,
                                                                  'enums.attractionCategory',
                                                                  row.category,
                                                              )
                                                            : '—'}
                                                    </Text>
                                                    <Text size='xs' c='dimmed' lh={1.3}>
                                                        {row.fame
                                                            ? enumLabel(
                                                                  t,
                                                                  'enums.attractionFame',
                                                                  row.fame,
                                                              )
                                                            : '—'}
                                                    </Text>
                                                </Stack>
                                            </Table.Td>
                                            <Table.Td>
                                                <Stack gap={4}>
                                                    <Text size='xs' c='dimmed'>
                                                        {t('pages.attractions.columns.mustSee')}{' '}
                                                        {row.mustSeeScore ?? '—'}
                                                    </Text>
                                                    <Badge
                                                        color={
                                                            reviewColor[row.reviewStatus] || 'gray'
                                                        }
                                                        variant='light'
                                                        size='sm'
                                                        style={{ width: 'fit-content' }}
                                                    >
                                                        {enumLabel(
                                                            t,
                                                            'enums.attractionReviewStatus',
                                                            row.reviewStatus,
                                                        )}
                                                    </Badge>
                                                </Stack>
                                            </Table.Td>
                                        </Table.Tr>
                                    );
                                })}
                                {!rows.length && (
                                    <Table.Tr>
                                        <Table.Td colSpan={5}>
                                            <Text c='dimmed' ta='center' py='md'>
                                                {t('pages.attractions.empty')}
                                            </Text>
                                        </Table.Td>
                                    </Table.Tr>
                                )}
                            </Table.Tbody>
                        </Table>
                    </Table.ScrollContainer>
                    <ListPagination page={page} totalItems={total} onChange={setPage} />
                </>
            )}

            <AttractionEditor
                opened={creating || editorId != null}
                attractionId={creating ? null : editorId}
                onClose={() => {
                    setCreating(false);
                    setEditorId(null);
                }}
                onSaved={refresh}
                onCreated={(id) => {
                    setCreating(false);
                    setEditorId(id);
                }}
                initialCityId={parseCityFilter(cityFilter)}
            />
        </Stack>
    );
};
